import { useEffect, useMemo, useState, type DragEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tickets } from '@repletoryreach/sdk-api';
import { ActionIcon, Badge, Button, Card, Divider, Group, Skeleton, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { Checkbox, NumberInput, Select, Switch, Textarea, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT, type Translate } from '../../../i18n/I18nProvider';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { TICKETS_KEY, categoryLabel, channelLabel, personName, ticketErrorText, useLookups, type Lookups } from '../lib/data';
import {
  CHANNELS, actionProblem, actionsForTrigger, fieldDef, fieldsForTrigger, moveItem, newAction, newCondition, opTakesList,
  opTakesNoValue, ruleDraft, ruleProblem, splitList, toRuleInput, type ActionDraft, type ConditionDraft, type RuleDraft
} from '../lib/rules';

const MUTED = 'var(--rr-color-text-muted)';
const RULES_KEY = [TICKETS_KEY, 'rules'];
const DRAG_TYPE = 'application/x-rr-rule'; // i18n-ignore: drag data type

export function RulesSection({ canEdit, limit }: { canEdit: boolean; limit: number | null | undefined }) {
  const t = useT();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: RULES_KEY, queryFn: () => tickets.listRules() });
  const [order, setOrder] = useState<tickets.Rule[]>([]);
  const [editing, setEditing] = useState<tickets.Rule | 'new' | null>(null);
  const [runsFor, setRunsFor] = useState<string | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);

  useEffect(() => { setOrder([...(q.data?.data ?? [])].sort((a, b) => a.sort_order - b.sort_order)); }, [q.data]);
  const activeCount = order.filter((r) => r.active).length;
  const atLimit = limit != null && activeCount >= limit;

  const reorder = useMutation({
    mutationFn: (rules: tickets.Rule[]) => tickets.reorderRules(rules.map((r) => r.id)),
    onSuccess: (res) => qc.setQueryData(RULES_KEY, res),
    onError: (e) => {
      notifyError(ticketErrorText(t, e), t('tickets.rules.title'));
      setOrder([...(q.data?.data ?? [])].sort((a, b) => a.sort_order - b.sort_order));
    }
  });

  const toggle = useMutation({
    mutationFn: (r: tickets.Rule) => tickets.updateRule(r.id, { active: !r.active }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: RULES_KEY });
      notifySuccess(t(res.data.active ? 'tickets.rules.toast.on' : 'tickets.rules.toast.off', { name: res.data.name }), t('tickets.rules.title'));
    },
    onError: (e) => notifyError(ticketErrorText(t, e), t('tickets.rules.title'))
  });

  function move(from: number, to: number) {
    const next = moveItem(order, from, to);
    if (next === order || from === to) return;
    setOrder(next);
    reorder.mutate(next);
  }

  function onDrop(e: DragEvent<HTMLElement>, to: number) {
    e.preventDefault();
    const from = Number(e.dataTransfer.getData(DRAG_TYPE));
    setDragging(null);
    if (Number.isInteger(from)) move(from, to);
  }

  return (
    <Stack gap="md">
      <Group gap="sm" justify="space-between" wrap="wrap" align="flex-start">
        <Stack gap="xs" style={{ flex: '1 1 320px' }}>
          <Title order={4}>{t('tickets.rules.title')}</Title>
          <Text role="bodySmall" c={MUTED}>{t('tickets.rules.intro')}</Text>
          <Text role="caption" c={MUTED}>
            {limit == null ? t('tickets.rules.noLimit', { active: activeCount }) : t('tickets.rules.limit', { active: activeCount, limit })}
          </Text>
        </Stack>
        {canEdit && <Button size="sm" data-guide="tickets-rules-add" onClick={() => setEditing('new')}>{t('tickets.rules.add')}</Button>}
      </Group>

      {q.error && (
        <Alert color="error" role="alert">
          <Group gap="sm" justify="space-between" wrap="wrap">
            <span>{ticketErrorText(t, q.error)}</span>
            <Button size="xs" variant="default" onClick={() => void q.refetch()}>{t('action.retry')}</Button>
          </Group>
        </Alert>
      )}
      {q.isPending && <Skeleton height={140} />}
      {q.isSuccess && order.length === 0 && (
        <Card withBorder>
          <EmptyState
            title={t('tickets.rules.empty')}
            description={t('tickets.rules.emptyHint')}
            action={canEdit ? <Button size="xs" onClick={() => setEditing('new')}>{t('tickets.rules.add')}</Button> : undefined}
          />
        </Card>
      )}

      <ol className="rr-tickets__rules" data-testid="rules-list">
        {order.map((r, i) => (
          <li
            key={r.id}
            className={`rr-tickets__rule${dragging === i ? ' is-dragging' : ''}${r.active ? '' : ' is-off'}`}
            draggable={canEdit && !reorder.isPending}
            onDragStart={(e) => { e.dataTransfer.setData(DRAG_TYPE, String(i)); e.dataTransfer.effectAllowed = 'move'; setDragging(i); }}
            onDragEnd={() => setDragging(null)}
            onDragOver={(e) => { if (canEdit) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } }}
            onDrop={(e) => onDrop(e, i)}
          >
            <Group gap="sm" justify="space-between" wrap="wrap" align="center">
              <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: '1 1 280px' }}>
                {canEdit && <span className="rr-tickets__grip" aria-hidden>⋮⋮</span>}
                <span className="rr-tickets__rule-order">{i + 1}</span>
                <Stack gap={2} style={{ minWidth: 0 }}>
                  <Text role="bodySmall" fw={600} lineClamp={1}>{r.name}</Text>
                  <Text role="caption" c={MUTED} lineClamp={2}>{ruleSummary(t, r)}</Text>
                </Stack>
              </Group>
              <Group gap="xs" wrap="wrap">
                {r.stop_after && <Badge size="xs" variant="light">{t('tickets.rules.stops')}</Badge>}
                <Switch
                  size="sm"
                  aria-label={t('tickets.rules.switchFor', { name: r.name })}
                  checked={r.active}
                  disabled={!canEdit || (toggle.isPending && toggle.variables?.id === r.id) || (!r.active && atLimit)}
                  title={!r.active && atLimit ? t('tickets.error.PLAN_LIMIT_REACHED') : undefined}
                  onChange={() => toggle.mutate(r)}
                />
                {canEdit && (
                  <>
                    <ActionIcon size="sm" variant="subtle" aria-label={t('tickets.moveUp')} disabled={i === 0 || reorder.isPending} onClick={() => move(i, i - 1)}>↑</ActionIcon>
                    <ActionIcon size="sm" variant="subtle" aria-label={t('tickets.moveDown')} disabled={i === order.length - 1 || reorder.isPending} onClick={() => move(i, i + 1)}>↓</ActionIcon>
                  </>
                )}
                <Button size="xs" variant="subtle" aria-expanded={runsFor === r.id} onClick={() => setRunsFor(runsFor === r.id ? null : r.id)}>
                  {t('tickets.rules.runs')}
                </Button>
                <Button size="xs" variant="default" onClick={() => setEditing(r)}>{canEdit ? t('action.edit') : t('action.view')}</Button>
              </Group>
            </Group>
            {runsFor === r.id && <RunsPanel rule={r} />}
          </li>
        ))}
      </ol>
      {canEdit && order.length > 1 && <Text role="caption" c={MUTED}>{t('tickets.rules.orderHint')}</Text>}

      <RuleDrawer
        target={editing}
        canEdit={canEdit}
        nextOrder={order.length}
        onClose={() => setEditing(null)}
        onSaved={() => void qc.invalidateQueries({ queryKey: RULES_KEY })}
      />
    </Stack>
  );
}

/** "When a message arrives · 2 conditions · Then: create a ticket, assign" */
function ruleSummary(t: Translate, r: tickets.Rule): string {
  const when = t(`tickets.trigger.${r.trigger}`, r.trigger);
  const conds = r.conditions.filter((c) => c.field !== 'minutes').length;
  const then = r.actions.map((a) => t(`tickets.action.${a.type}`, a.type).toLowerCase()).join(', ');
  return [when, conds ? t('tickets.rules.conditionsCount', { count: conds }) : t('tickets.rules.always'), then ? t('tickets.rules.then', { actions: then }) : '']
    .filter(Boolean).join(' · ');
}

function RunsPanel({ rule }: { rule: tickets.Rule }) {
  const t = useT();
  const q = useQuery({ queryKey: [...RULES_KEY, rule.id, 'runs'], queryFn: () => tickets.listRuleRuns(rule.id, { limit: 20 }), refetchInterval: 60_000 });
  const runs = q.data?.data ?? [];
  return (
    <div className="rr-tickets__runs" data-testid="rule-runs">
      <Text role="caption" fw={600}>{t('tickets.rules.recentRuns')}</Text>
      {q.isPending && <Skeleton height={40} />}
      {q.error && (
        <Group gap="sm" wrap="wrap">
          <Text role="caption" c="var(--rr-color-error)">{ticketErrorText(t, q.error)}</Text>
          <Button size="xs" variant="subtle" onClick={() => void q.refetch()}>{t('action.retry')}</Button>
        </Group>
      )}
      {q.isSuccess && runs.length === 0 && <Text role="caption" c={MUTED}>{t('tickets.rules.noRuns')}</Text>}
      {runs.map((run) => (
        <Group key={run.id} gap="sm" wrap="wrap" className="rr-tickets__run">
          <Text role="caption" title={formatDateTime(run.at)}>{formatRelative(run.at)}</Text>
          {run.outcome && <Badge size="xs" variant="light" color={run.outcome === 'failed' ? 'red' : run.outcome === 'skipped' ? 'gray' : 'green'}>{t(`tickets.rules.outcome.${run.outcome}`, run.outcome)}</Badge>}
          {run.ticket_id && run.ticket_number && <Link to={`/tickets/${encodeURIComponent(run.ticket_id)}`}>{run.ticket_number}</Link>}
          {run.detail && <Text role="caption" c={MUTED}>{run.detail}</Text>}
        </Group>
      ))}
    </div>
  );
}

// ── The builder ───────────────────────────────────────────────────────────

function RuleDrawer({ target, canEdit, nextOrder, onClose, onSaved }: {
  target: tickets.Rule | 'new' | null;
  canEdit: boolean;
  nextOrder: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const lk = useLookups();
  const existing = target && target !== 'new' ? target : null;
  const open = target !== null;
  const [d, setD] = useState<RuleDraft>(() => ruleDraft(null));
  const [initial, setInitial] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const draft = ruleDraft(existing);
    setD(draft);
    setInitial(JSON.stringify(draft));
    setTried(false);
    setError(null);
  }, [open, existing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch: Partial<RuleDraft>) => setD((x) => ({ ...x, ...patch }));
  const dirty = open && JSON.stringify(d) !== initial;
  const problem = ruleProblem(d);
  const ro = !canEdit;
  const offered = fieldsForTrigger(d.trigger);
  const allowedActions = actionsForTrigger(d.trigger);

  function requestClose() {
    if (!dirty || ro) { onClose(); return; }
    openConfirmModal({
      title: t('tickets.discard.title'),
      children: <Text role="bodySmall">{t('tickets.discard.body')}</Text>,
      labels: { confirm: t('action.discard'), cancel: t('action.keep') },
      confirmProps: { color: 'red' },
      onConfirm: onClose
    });
  }

  async function save() {
    setTried(true);
    if (problem) return;
    setBusy(true);
    setError(null);
    try {
      const body = toRuleInput(d, existing?.sort_order ?? nextOrder);
      const res = existing ? await tickets.updateRule(existing.id, body) : await tickets.createRule(body);
      notifySuccess(t(existing ? 'tickets.rules.toast.saved' : 'tickets.rules.toast.created', { name: res.data.name }), t('tickets.rules.title'));
      onSaved();
      onClose();
    } catch (e) {
      setError(ticketErrorText(t, e));
    } finally {
      setBusy(false);
    }
  }

  function remove() {
    if (!existing) return;
    openConfirmModal({
      title: t('tickets.settings.delete.title', { name: existing.name }),
      children: <Text role="bodySmall">{t('tickets.rules.deleteBody', { name: existing.name })}</Text>,
      labels: { confirm: t('action.delete'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await tickets.deleteRule(existing.id);
          notifySuccess(t('tickets.settings.toast.deleted', { name: existing.name }), t('tickets.rules.title'));
          onSaved();
          onClose();
        } catch (e) {
          notifyError(ticketErrorText(t, e), t('tickets.rules.title'));
        }
      }
    });
  }

  const setCondition = (i: number, c: ConditionDraft) => set({ conditions: d.conditions.map((x, j) => (j === i ? c : x)) });
  const setAction = (i: number, a: ActionDraft) => set({ actions: d.actions.map((x, j) => (j === i ? a : x)) });
  const hidden = d.conditions.filter((c) => !fieldDef(c.field)?.triggers.includes(d.trigger)).length;

  return (
    <Drawer opened={open} onClose={requestClose} title={existing ? existing.name : t('tickets.rules.new')} size="xl">
      <form onSubmit={(e) => { e.preventDefault(); if (canEdit) void save(); }}>
        <Stack gap="md" data-testid="rule-builder">
          {ro && <Alert color="info">{t('tickets.readOnly')}</Alert>}
          <div data-guide="rule-name">
            <TextInput label={t('common.name')} value={d.name} onChange={(e) => set({ name: e.currentTarget.value })} required maxLength={120} disabled={ro}
              error={tried && problem === 'tickets.rules.error.name' ? t(problem) : undefined} />
          </div>

          <section className="rr-tickets__builder-block" data-guide="rule-when">
            <Text role="subtitle" fw={600}>{t('tickets.rules.when')}</Text>
            <Select
              aria-label={t('tickets.rules.when')}
              value={d.trigger}
              onChange={(e) => set({ trigger: e.currentTarget.value as tickets.RuleTrigger })}
              data={tickets.RULE_TRIGGERS.map((tr) => ({ value: tr, label: t(`tickets.trigger.${tr}`) }))}
              description={t(`tickets.trigger.hint.${d.trigger}`)}
              disabled={ro}
            />
            {d.trigger === 'unanswered' && (
              <NumberInput label={t('tickets.rules.minutes')} value={d.minutes} onChange={(v) => set({ minutes: v })} min={1} max={10080} disabled={ro}
                description={t('tickets.rules.minutesHint')}
                error={tried && problem === 'tickets.rules.error.minutes' ? t(problem) : undefined} />
            )}
          </section>

          <section className="rr-tickets__builder-block" data-guide="rule-if">
            <Group gap="sm" justify="space-between">
              <Text role="subtitle" fw={600}>{t('tickets.rules.if')}</Text>
              <Text role="caption" c={MUTED}>{t('tickets.rules.allMustMatch')}</Text>
            </Group>
            {d.conditions.length === 0 && <Text role="bodySmall" c={MUTED}>{t('tickets.rules.noConditions')}</Text>}
            {d.conditions.map((c, i) => (
              <ConditionRow
                key={i}
                c={c}
                lk={lk}
                trigger={d.trigger}
                disabled={ro}
                invalid={tried && problem === 'tickets.rules.error.condition'}
                onChange={(next) => setCondition(i, next)}
                onRemove={() => set({ conditions: d.conditions.filter((_, j) => j !== i) })}
              />
            ))}
            {hidden > 0 && <Text role="caption" c="var(--rr-color-warning)">{t('tickets.rules.hiddenConditions', { count: hidden })}</Text>}
            {tried && problem === 'tickets.rules.error.condition' && <Text role="caption" c="var(--rr-color-error)">{t(problem)}</Text>}
            {canEdit && offered.length > 0 && (
              <div><Button size="xs" variant="subtle" onClick={() => set({ conditions: [...d.conditions, newCondition(d.trigger)] })}>{t('tickets.rules.addCondition')}</Button></div>
            )}
          </section>

          <section className="rr-tickets__builder-block" data-guide="rule-then">
            <Text role="subtitle" fw={600}>{t('tickets.rules.thenDo')}</Text>
            {d.actions.length === 0 && <Text role="bodySmall" c={MUTED}>{t('tickets.rules.noActions')}</Text>}
            {d.actions.map((a, i) => (
              <ActionRow
                key={i}
                a={a}
                lk={lk}
                allowed={allowedActions}
                disabled={ro}
                showError={tried}
                onChange={(next) => setAction(i, next)}
                onRemove={() => set({ actions: d.actions.filter((_, j) => j !== i) })}
                onUp={i > 0 ? () => set({ actions: moveItem(d.actions, i, i - 1) }) : undefined}
              />
            ))}
            {tried && (problem === 'tickets.rules.error.noActions' || problem === 'tickets.rules.error.createTicketTrigger') && (
              <Text role="caption" c="var(--rr-color-error)">{t(problem)}</Text>
            )}
            {canEdit && (
              <Select
                size="xs"
                aria-label={t('tickets.rules.addAction')}
                value=""
                onChange={(e) => { const v = e.currentTarget.value as tickets.ActionType; if (v) set({ actions: [...d.actions, newAction(v)] }); }}
                data={[{ value: '', label: t('tickets.rules.addAction') }, ...allowedActions.map((a) => ({ value: a, label: t(`tickets.action.${a}`) }))]}
              />
            )}
          </section>

          <Divider />
          <Stack gap="md" data-guide="rule-switches">
            <Switch label={t('tickets.rules.stopAfter')} description={t('tickets.rules.stopAfterHint')} checked={d.stop_after} onChange={(on) => set({ stop_after: on })} disabled={ro} />
            <Switch label={t('tickets.rules.active')} description={t('tickets.rules.activeHint')} checked={d.active} onChange={(on) => set({ active: on })} disabled={ro} />
          </Stack>

          {error && <Alert color="error" role="alert">{error}</Alert>}
          <Group gap="sm" justify="space-between" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
            <div>{canEdit && existing && <Button variant="subtle" color="red" onClick={remove}>{t('action.delete')}</Button>}</div>
            <Group gap="sm">
              <Button variant="default" onClick={requestClose}>{canEdit ? t('action.cancel') : t('action.close')}</Button>
              {canEdit && <Button type="submit" loading={busy} data-guide="rule-save">{existing ? t('action.saveChanges') : t('tickets.rules.create')}</Button>}
            </Group>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

/** Every custom field of every type, as `ticket.field.<key>` choices. */
function customFieldChoices(lk: Lookups): { value: string; label: string; field: tickets.TypeField }[] {
  const seen = new Map<string, { value: string; label: string; field: tickets.TypeField }>();
  for (const ty of lk.types) {
    for (const f of ty.fields) {
      if (!seen.has(f.key)) seen.set(f.key, { value: `ticket.field.${f.key}`, label: f.label, field: f });
    }
  }
  return [...seen.values()];
}

function ConditionRow({ c, lk, trigger, disabled, invalid, onChange, onRemove }: {
  c: ConditionDraft;
  lk: Lookups;
  trigger: tickets.RuleTrigger;
  disabled: boolean;
  invalid: boolean;
  onChange: (c: ConditionDraft) => void;
  onRemove: () => void;
}) {
  const t = useT();
  const def = fieldDef(c.field);
  const custom = useMemo(() => customFieldChoices(lk), [lk]);
  const offered = fieldsForTrigger(trigger);
  const fieldOptions = offered.flatMap((f) => (f.field === 'ticket.field'
    ? custom.map((x) => ({ value: x.value, label: t('tickets.cond.customField', { label: x.label }) }))
    : [{ value: f.field, label: t(`tickets.cond.${f.field}`) }]));
  if (!fieldOptions.some((o) => o.value === c.field)) fieldOptions.unshift({ value: c.field, label: c.field.startsWith('ticket.field.') ? t('tickets.cond.customField', { label: c.field.slice(13) }) : t(`tickets.cond.${c.field}`, c.field) });

  function changeField(field: string) {
    const next = fieldDef(field);
    const op = next && next.ops.includes(c.op) ? c.op : next?.ops[0] ?? 'eq';
    onChange({ field, op, value: opTakesList(op) ? [] : '' });
  }
  function changeOp(op: tickets.ConditionOp) {
    const wasList = opTakesList(c.op);
    const isList = opTakesList(op);
    const value = wasList === isList ? c.value : isList ? splitList(c.value) : (Array.isArray(c.value) ? c.value[0] ?? '' : c.value);
    onChange({ ...c, op, value });
  }

  const customField = c.field.startsWith('ticket.field.') ? custom.find((x) => x.value === c.field)?.field : undefined;
  const unfinished = invalid && !opTakesNoValue(c.op) && splitList(c.value).length === 0;

  return (
    <div className={`rr-tickets__builder-row${unfinished ? ' is-invalid' : ''}`}>
      <Group gap="xs" align="flex-end" wrap="wrap">
        <Select size="sm" aria-label={t('tickets.rules.field')} value={c.field} disabled={disabled} onChange={(e) => changeField(e.currentTarget.value)} data={fieldOptions} />
        <Select size="sm" aria-label={t('tickets.rules.op')} value={c.op} disabled={disabled} onChange={(e) => changeOp(e.currentTarget.value as tickets.ConditionOp)}
          data={(def?.ops ?? ['eq']).map((op) => ({ value: op, label: t(`tickets.op.${op}`) }))} />
        {!opTakesNoValue(c.op) && (
          <div style={{ flex: '1 1 200px', minWidth: 0 }}>
            <ValuePicker c={c} kind={def?.kind ?? 'tag'} lk={lk} customField={customField} disabled={disabled} onChange={(value) => onChange({ ...c, value })} />
          </div>
        )}
        {!disabled && <Button size="xs" variant="subtle" color="red" onClick={onRemove} aria-label={t('tickets.rules.removeCondition')}>{t('action.remove')}</Button>}
      </Group>
    </div>
  );
}

/** The value of a condition, picked from what the field can hold. */
function ValuePicker({ c, kind, lk, customField, disabled, onChange }: {
  c: ConditionDraft;
  kind: string;
  lk: Lookups;
  customField?: tickets.TypeField;
  disabled: boolean;
  onChange: (v: string | string[]) => void;
}) {
  const t = useT();
  const list = opTakesList(c.op);
  const single = Array.isArray(c.value) ? c.value[0] ?? '' : c.value;
  const label = t('tickets.rules.value');

  let options: { value: string; label: string }[] | null = null;
  if (kind === 'channel') options = CHANNELS.map((ch) => ({ value: ch, label: channelLabel(t, ch) }));
  else if (kind === 'type') options = lk.types.map((x) => ({ value: x.id, label: x.name }));
  else if (kind === 'priority') options = lk.priorities.map((x) => ({ value: x.id, label: x.name }));
  else if (kind === 'team') options = lk.teams.map((x) => ({ value: x.id, label: x.name }));
  else if (kind === 'category') options = tickets.STATUS_CATEGORIES.map((x) => ({ value: x, label: categoryLabel(t, x) }));
  else if (kind === 'business_hours') options = [{ value: 'open', label: t('tickets.cond.hoursOpen') }, { value: 'closed', label: t('tickets.cond.hoursClosed') }];
  else if (kind === 'field' && customField && (customField.type === 'select' || customField.type === 'multi_select')) {
    options = (customField.options ?? []).map((o) => ({ value: o, label: o }));
  } else if (kind === 'field' && customField?.type === 'yes_no') {
    options = [{ value: 'true', label: t('common.yes') }, { value: 'false', label: t('common.no') }];
  }

  if (options) {
    if (list) {
      const chosen = Array.isArray(c.value) ? c.value : splitList(c.value);
      return (
        <Group gap="sm" wrap="wrap" role="group" aria-label={label}>
          {options.length === 0 && <Text role="caption" c={MUTED}>{t('tickets.rules.nothingToPick')}</Text>}
          {options.map((o) => (
            <Checkbox key={o.value} size="sm" label={o.label} checked={chosen.includes(o.value)} disabled={disabled}
              onChange={(on) => onChange(on ? [...chosen, o.value] : chosen.filter((x) => x !== o.value))} />
          ))}
        </Group>
      );
    }
    return (
      <Select size="sm" aria-label={label} value={single} disabled={disabled} onChange={(e) => onChange(e.currentTarget.value)}
        data={[{ value: '', label: t('tickets.field.choose') }, ...options]} />
    );
  }
  if (kind === 'number' || (kind === 'field' && customField?.type === 'number') || c.op === 'gte' || c.op === 'lte') {
    return (
      <NumberInput size="sm" aria-label={label} value={single === '' ? '' : Number(single)} disabled={disabled} min={0}
        onChange={(v) => onChange(v === '' ? '' : String(v))}
        description={kind === 'number' ? t('tickets.rules.minutesUnit') : undefined} />
    );
  }
  const text = Array.isArray(c.value) ? c.value.join(', ') : c.value;
  return (
    <TextInput size="sm" aria-label={label} value={text} disabled={disabled} maxLength={500}
      onChange={(e) => onChange(e.currentTarget.value)}
      placeholder={kind === 'keywords' ? t('tickets.rules.keywordsPlaceholder') : list ? t('tickets.rules.listPlaceholder') : undefined}
      description={kind === 'keywords' ? t('tickets.rules.keywordsHint') : list ? t('tickets.rules.listHint') : undefined} />
  );
}

function ActionRow({ a, lk, allowed, disabled, showError, onChange, onRemove, onUp }: {
  a: ActionDraft;
  lk: Lookups;
  allowed: tickets.ActionType[];
  disabled: boolean;
  showError: boolean;
  onChange: (a: ActionDraft) => void;
  onRemove: () => void;
  onUp?: () => void;
}) {
  const t = useT();
  const problem = showError ? actionProblem(a) : null;
  const notAllowed = !allowed.includes(a.type);
  const people = lk.people.map((p) => ({ value: p.id, label: personName(p) }));
  const teams = lk.teams.map((x) => ({ value: x.id, label: x.name }));
  const none = (label: string) => [{ value: '', label }];

  let body: React.ReactNode = null;
  switch (a.type) {
    case 'create_ticket':
      body = (
        <Stack gap="xs">
          <Group gap="xs" grow wrap="wrap" align="flex-start">
            <Select size="sm" label={t('tickets.field.type')} value={a.type_id ?? ''} disabled={disabled} onChange={(e) => onChange({ ...a, type_id: e.currentTarget.value || null })}
              data={[...none(t('tickets.rules.firstType')), ...lk.types.map((x) => ({ value: x.id, label: x.name }))]} />
            <Select size="sm" label={t('tickets.field.priority')} value={a.priority_id ?? ''} disabled={disabled} onChange={(e) => onChange({ ...a, priority_id: e.currentTarget.value || null })}
              data={[...none(t('tickets.rules.typeDefault')), ...lk.priorities.map((x) => ({ value: x.id, label: x.name }))]} />
            <Select size="sm" label={t('tickets.field.team')} value={a.team_id ?? ''} disabled={disabled} onChange={(e) => onChange({ ...a, team_id: e.currentTarget.value || null })}
              data={[...none(t('tickets.rules.typeDefault')), ...teams]} />
          </Group>
          <TextInput size="sm" label={t('tickets.field.subject')} value={a.subject ?? ''} disabled={disabled} maxLength={200}
            onChange={(e) => onChange({ ...a, subject: e.currentTarget.value })}
            description={t('tickets.rules.subjectHint')} />
        </Stack>
      );
      break;
    case 'assign':
      body = (
        <Stack gap="xs">
          <Group gap="xs" grow wrap="wrap" align="flex-start">
            <Select size="sm" label={t('tickets.field.team')} value={a.team_id ?? ''} disabled={disabled}
              onChange={(e) => onChange({ ...a, team_id: e.currentTarget.value || null, round_robin: e.currentTarget.value ? a.round_robin : false })}
              data={[...none(t('tickets.rules.keepTeam')), ...teams]} />
            <Select size="sm" label={t('tickets.rules.person')} value={a.user_id ?? ''} disabled={disabled || !!a.round_robin}
              onChange={(e) => onChange({ ...a, user_id: e.currentTarget.value || null })}
              data={[...none(t('tickets.rules.nobodyInParticular')), ...people]} />
          </Group>
          <Checkbox size="sm" label={t('tickets.rules.roundRobin')} description={t('tickets.rules.roundRobinHint')} checked={!!a.round_robin}
            disabled={disabled || !a.team_id} onChange={(on) => onChange({ ...a, round_robin: on, user_id: on ? null : a.user_id })} />
        </Stack>
      );
      break;
    case 'set_status':
      body = (
        <Select size="sm" aria-label={t('common.status')} value={a.status_id} disabled={disabled} onChange={(e) => onChange({ ...a, status_id: e.currentTarget.value })}
          data={[...none(t('tickets.field.choose')), ...lk.statuses.map((s) => ({
            value: s.id,
            label: `${s.name} (${categoryLabel(t, s.category)}${s.type_id ? `, ${lk.typeById.get(s.type_id)?.name ?? ''}` : ''})`
          }))]} />
      );
      break;
    case 'set_priority':
      body = (
        <Select size="sm" aria-label={t('tickets.field.priority')} value={a.priority_id} disabled={disabled} onChange={(e) => onChange({ ...a, priority_id: e.currentTarget.value })}
          data={[...none(t('tickets.field.choose')), ...lk.priorities.map((x) => ({ value: x.id, label: x.name }))]} />
      );
      break;
    case 'add_tag':
      body = <TextInput size="sm" aria-label={t('tickets.field.tag')} value={a.tag} disabled={disabled} maxLength={60} onChange={(e) => onChange({ ...a, tag: e.currentTarget.value })} />;
      break;
    case 'set_field': {
      const choices = customFieldChoices(lk);
      body = (
        <Group gap="xs" grow wrap="wrap" align="flex-start">
          <Select size="sm" label={t('tickets.rules.whichField')} value={a.key} disabled={disabled} onChange={(e) => onChange({ ...a, key: e.currentTarget.value })}
            data={[...none(t('tickets.field.choose')), ...choices.map((x) => ({ value: x.field.key, label: x.label }))]} />
          <TextInput size="sm" label={t('tickets.rules.value')} value={a.value == null ? '' : String(a.value)} disabled={disabled} maxLength={300}
            onChange={(e) => onChange({ ...a, value: e.currentTarget.value })} />
        </Group>
      );
      break;
    }
    case 'notify':
      body = (
        <Group gap="xs" grow wrap="wrap" align="flex-start">
          <Select size="sm" label={t('tickets.rules.notifyWho')} value={a.to} disabled={disabled}
            onChange={(e) => onChange({ ...a, to: e.currentTarget.value as 'team' | 'assignee' | 'user' })}
            data={[
              { value: 'assignee', label: t('tickets.rules.notify.assignee') },
              { value: 'team', label: t('tickets.rules.notify.team') },
              { value: 'user', label: t('tickets.rules.notify.user') }
            ]} />
          {a.to === 'user' && (
            <Select size="sm" label={t('tickets.rules.person')} value={a.user_id ?? ''} disabled={disabled} onChange={(e) => onChange({ ...a, user_id: e.currentTarget.value || null })}
              data={[...none(t('tickets.field.choose')), ...people]} />
          )}
        </Group>
      );
      break;
    case 'reply_client':
      body = (
        <Textarea size="sm" aria-label={t('tickets.rules.replyText')} value={a.text} disabled={disabled} minRows={2} maxLength={1000}
          onChange={(e) => onChange({ ...a, text: e.currentTarget.value })} description={t('tickets.rules.replyHint')} />
      );
      break;
    case 'add_task':
      body = (
        <Group gap="xs" wrap="wrap" align="flex-start">
          <Select size="sm" label={t('tickets.task.kind')} value={a.kind} disabled={disabled} onChange={(e) => onChange({ ...a, kind: e.currentTarget.value as tickets.TaskKind })}
            data={(['call', 'custom'] as const).map((k) => ({ value: k, label: t(`tickets.task.kind.${k}`) }))} />
          <TextInput size="sm" label={t('tickets.task.title')} value={a.title} disabled={disabled} maxLength={200} style={{ flex: '1 1 200px' }}
            onChange={(e) => onChange({ ...a, title: e.currentTarget.value })} />
          <NumberInput size="sm" label={t('tickets.rules.dueIn')} value={a.due_minutes ?? ''} disabled={disabled} min={0} max={100000}
            onChange={(v) => onChange({ ...a, due_minutes: v === '' ? null : v })} description={t('tickets.rules.minutesUnit')} />
        </Group>
      );
      break;
    case 'close':
      body = <Text role="caption" c={MUTED}>{t('tickets.rules.closeHint')}</Text>;
      break;
  }

  return (
    <div className={`rr-tickets__builder-row${problem || notAllowed ? ' is-invalid' : ''}`}>
      <Group gap="xs" justify="space-between" wrap="nowrap">
        <Badge variant="light" size="sm">{t(`tickets.action.${a.type}`)}</Badge>
        {!disabled && (
          <Group gap="xs">
            {onUp && <ActionIcon size="sm" variant="subtle" aria-label={t('tickets.moveUp')} onClick={onUp}>↑</ActionIcon>}
            <Button size="xs" variant="subtle" color="red" onClick={onRemove} aria-label={t('tickets.rules.removeAction')}>{t('action.remove')}</Button>
          </Group>
        )}
      </Group>
      {body}
      {problem && <Text role="caption" c="var(--rr-color-error)">{t(problem)}</Text>}
      {notAllowed && <Text role="caption" c="var(--rr-color-error)">{t('tickets.rules.error.createTicketTrigger')}</Text>}
    </div>
  );
}
